import axios from 'axios';
import { toast } from 'react-hot-toast';
import { BaseApi } from './baseApi';
import { tokenManager } from './tokenManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const BOT_API_URL = process.env.REACT_APP_BOT_API_URL || 'https://bot.horseracingoracle.com';

// Create API instance for main API
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Create API instance for bot API with longer timeout
const botApi = axios.create({
  baseURL: BOT_API_URL,
  timeout: 60000, // Increased to 60 seconds
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor for both instances
const addRequestInterceptor = (instance: any) => {
  instance.interceptors.request.use(async (config: any) => {
    try {
      // Get valid token (refreshed if needed)
      const token = await tokenManager.getValidToken();
      if (!token) {
        throw new Error('No valid token available');
      }
      config.headers.Authorization = `Bearer ${token}`;
      
      // Add CSRF token for non-GET requests
      const method = config.method?.toLowerCase();
      if (method && ['post', 'put', 'patch', 'delete'].includes(method)) {
        const csrfToken = instance.defaults.headers.common['X-CSRF-Token'];
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }
      
      return config;
    } catch (error) {
      // If we can't get a valid token, reject the request
      return Promise.reject(error);
    }
  });
};

// Add response interceptor for both instances
const addResponseInterceptor = (instance: any) => {
  instance.interceptors.response.use(
    (response: any) => {
      // Get CSRF token from header
      const csrfToken = response.headers['x-csrf-token'];
      if (csrfToken) {
        instance.defaults.headers.common['X-CSRF-Token'] = csrfToken;
      }
      return response;
    },
    async (error: any) => {
      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Please try again.');
        return Promise.reject(error);
      }

      // Handle other errors
      if (error.response?.status !== 401) {
        const errorMessage = error.response?.data?.detail || 'An unexpected error occurred.';
        toast.error(errorMessage);
      }

      return Promise.reject(error);
    }
  );
};

// Add interceptors to both instances
addRequestInterceptor(api);
addRequestInterceptor(botApi);
addResponseInterceptor(api);
addResponseInterceptor(botApi);

// Create a specific instance for races endpoint with even longer timeout
const racesApi = axios.create({
  baseURL: BOT_API_URL,
  timeout: 90000, // 90 seconds for races endpoint
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  }
});

addRequestInterceptor(racesApi);
addResponseInterceptor(racesApi);

// Simple in-memory cache
const cache = new Map<string, { data: any; timestamp: number }>();

export const getAccountDetails = async () => {
  const cacheKey = 'account-details';
  const cacheDuration = 5 * 60 * 1000; // 5 minutes
  
  // Check cache
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < cacheDuration) {
    return cached.data;
  }
  
  // If not in cache or expired, fetch fresh data
  const response = await botApi.get('/api/api-keys/account-details');
  
  // Update cache
  cache.set(cacheKey, {
    data: response.data,
    timestamp: Date.now()
  });
  
  return response.data;
};

export const getTodaysRaces = async () => {
  const cacheKey = 'todays-races';
  const cacheDuration = 2 * 60 * 1000; // 2 minutes
  
  // Check cache
  const cached = cache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < cacheDuration) {
    return cached.data;
  }
  
  // If not in cache or expired, fetch fresh data
  const response = await botApi.get('/api/api-keys/todays-races');
  
  // Update cache
  cache.set(cacheKey, {
    data: response.data,
    timestamp: Date.now()
  });
  
  return response.data;
};

export const getBotStatus = async () => {
  return BaseApi.get('/api/bot/status', botApi, {
    cache: true,
    cacheTTL: 5000, // Cache for 5 seconds
  });
};

export const getBotSettings = async () => {
  return BaseApi.get('/api/bot/settings/percentage', botApi, {
    cache: true,
    cacheTTL: 30000, // Cache for 30 seconds
  });
};

export { api, botApi, racesApi };

// Default export for backward compatibility
export default api; 